import { FormattedMessage } from "react-intl";
import { Navigate, Route, Routes } from "react-router-dom";

import { Sidebar, SidebarSectionHeader, SidebarV2 } from "common/sidebar";
import { userFullName } from "util/user";
import { useIAnav, useOrganizationBrandName } from "util/feature_detection";
import { useHeadingDetails } from "common/settingsv2/common";
import { Feature } from "graphql_globals";

import {
  OrganizationSettings,
  SettingsSidebarOrgLink,
  ORG_ROUTE,
} from "../sidebar_settings/organization";
import {
  OrganizationOverview,
  OrganizationOverviewTab,
  ORG_OVERVIEW_ROUTE,
} from "../sidebar_settings/organization/overview";
import {
  OrganizationSharedInbox,
  OrganizationSharedInboxTab,
  ORG_SHARED_INBOX_ROUTE,
} from "../sidebar_settings/organization/shared_inbox";
import {
  OrganizationEligibility,
  OrganizationEligibilityTab,
  ORG_ELIGIBILITY_ROUTE,
} from "../sidebar_settings/organization/eligibility";
import {
  BillingSettings,
  SettingsSidebarBillingLink,
  BILLING_ROUTE,
} from "../sidebar_settings/billing";
import {
  BILLING_PLAN_DETAILS_ROUTE,
  PlanDetails,
  PlanDetailsTab,
} from "../sidebar_settings/billing/plan_details";
import {
  PaymentSettings,
  PaymentSettingsTab,
  BILLING_PAYMENT_SETTINGS_ROUTE,
} from "../sidebar_settings/billing/payment_settings";
import {
  BillingContact,
  BillingContactTab,
  BILLING_BILLING_CONTACT_ROUTE,
} from "../sidebar_settings/billing/billing_contact";
import {
  RecentPayments,
  RecentPaymentsTab,
  BILLING_RECENT_PAYMENTS_ROUTE,
} from "../sidebar_settings/billing/recent_payments";
import {
  TransactionSettings,
  SettingsSidebarTransactionsLink,
  TRANSACTIONS_ROUTE,
} from "../sidebar_settings/transaction";
import {
  SecuritySettings,
  SettingsSidebarSecurityLink,
  SECURITY_ROUTE,
} from "../sidebar_settings/security";
import LoginSecurity from "../sidebar_settings/security/login_security";
import {
  SignerSettings,
  SignerSettingsTab,
  TRANSACTIONS_SIGNER_SETTINGS_ROUTE,
} from "../sidebar_settings/transaction/signer_settings";
import {
  NotaryInstructions,
  NotaryInstructionsTab,
  TRANSACTIONS_NOTARY_INSTRUCTIONS_ROUTE,
} from "../sidebar_settings/transaction/notary_instructions";
import {
  ProfileSettings,
  SettingsSidebarProfileLink,
  PROFILE_ROUTE,
} from "../sidebar_settings/profile";
import {
  ProfileOverview,
  ProfileOverviewTab,
  PROFILE_OVERVIEW_ROUTE,
} from "../sidebar_settings/profile/overview";
import { TermsSettings, SettingsSidebarTermsLink, TERMS_ROUTE } from "../sidebar_settings/terms";
import { TermsAndConditions } from "../sidebar_settings/terms/terms";
import {
  BrandSettings as BrandSettingsWrapper,
  SettingsSidebarBrandCustomizationLink,
  BRAND_SETTINGS_ROOT_PREFIX,
} from "../sidebar_settings/brand";
import { BrandSettings, BRAND_SETTINGS_MAIN_ROUTE } from "../sidebar_settings/brand/settings";
import {
  LogoAndHeaderTab,
  LogoAndHeaderBrandSettings,
  LOGO_HEADER_BRAND_ROUTE,
} from "../sidebar_settings/brand/settings/logo_and_header";
import {
  BrandColorsTab,
  BrandColorsSettings,
  BRAND_COLORS_ROUTE,
} from "../sidebar_settings/brand/settings/brand_colors";
import {
  WebsiteCustomization,
  BRAND_SETTINGS_CUSTOMIZE_ROUTE,
} from "../sidebar_settings/brand/customization";
import {
  IntegrationsSettings as IntegrationSettingsContainer,
  INTEGRATIONS_ROUTE,
  SettingsSidebarIntegrationsLink,
} from "../sidebar_settings/integrations";
import IntegrationSettings from "../sidebar_settings/integrations/settings";
import {
  API_KEYS_SETTINGS_ROOT_PREFIX,
  SettingsSidebarApiKeysLink,
  ApiKeys,
} from "../sidebar_settings/api_keys";
import CustomizeLandingPage, {
  BRAND_SETTINGS_CUSTOMIZE_LANDING_ROUTE,
  CustomizeLandingPageTab,
} from "../sidebar_settings/brand/customization/landing_page";

function TitleAdminSidebar() {
  const { orgName, orgAddress, userName, userEmail, featureList } = useHeadingDetails();
  const orgSettingsOnly = useIAnav();
  return orgSettingsOnly ? (
    <SidebarV2
      title={
        <FormattedMessage id="75f8f276-419a-4839-9ee5-df88092adddb" defaultMessage="Settings" />
      }
      wide
    >
      <SidebarSectionHeader title={orgName} />
      <SettingsSidebarOrgLink />
      <SettingsSidebarBillingLink />
      <SettingsSidebarTransactionsLink />
      <SettingsSidebarSecurityLink />
      <SettingsSidebarBrandCustomizationLink />
      <SettingsSidebarIntegrationsLink />
      {featureList.includes(Feature.PARTNER_API) && <SettingsSidebarApiKeysLink />}
    </SidebarV2>
  ) : (
    <Sidebar
      title={
        <FormattedMessage id="c7b087d0-0a64-4c3d-bf15-04394b2a2cf9" defaultMessage="Settings" />
      }
      wide
    >
      <SidebarSectionHeader title={orgName} subtitle={orgAddress} />
      <SettingsSidebarOrgLink />
      <SettingsSidebarBillingLink />
      <SettingsSidebarTransactionsLink />
      <SettingsSidebarSecurityLink />
      <SettingsSidebarBrandCustomizationLink />
      <SettingsSidebarIntegrationsLink />
      {featureList.includes(Feature.PARTNER_API) && <SettingsSidebarApiKeysLink />}

      <SidebarSectionHeader title={userFullName(userName)} subtitle={userEmail} />
      <SettingsSidebarProfileLink />
      <SettingsSidebarTermsLink />
    </Sidebar>
  );
}

export default function TitleAdminSettings() {
  const orgBrandNameEnabled = useOrganizationBrandName();
  return (
    <>
      <TitleAdminSidebar />
      <Routes>
        <Route
          path={ORG_ROUTE}
          element={
            <OrganizationSettings
              tabs={
                <>
                  <OrganizationOverviewTab />
                  <OrganizationSharedInboxTab />
                  <OrganizationEligibilityTab />
                </>
              }
            />
          }
        >
          <Route path={ORG_OVERVIEW_ROUTE} element={<OrganizationOverview />} />
          <Route path={ORG_SHARED_INBOX_ROUTE} element={<OrganizationSharedInbox />} />
          <Route path={ORG_ELIGIBILITY_ROUTE} element={<OrganizationEligibility />} />
          <Route index element={<Navigate to={ORG_OVERVIEW_ROUTE} replace />} />
        </Route>
        <Route
          path={BILLING_ROUTE}
          element={
            <BillingSettings
              tabs={
                <>
                  <PlanDetailsTab />
                  <PaymentSettingsTab />
                  <BillingContactTab />
                  <RecentPaymentsTab />
                </>
              }
            />
          }
        >
          <Route path={BILLING_PLAN_DETAILS_ROUTE} element={<PlanDetails />} />
          <Route path={BILLING_PAYMENT_SETTINGS_ROUTE} element={<PaymentSettings />} />
          <Route path={BILLING_BILLING_CONTACT_ROUTE} element={<BillingContact />} />
          <Route path={BILLING_RECENT_PAYMENTS_ROUTE} element={<RecentPayments />} />
          <Route index element={<Navigate to={BILLING_PLAN_DETAILS_ROUTE} replace />} />
        </Route>
        <Route
          path={TRANSACTIONS_ROUTE}
          element={
            <TransactionSettings
              tabs={
                <>
                  <SignerSettingsTab />
                  <NotaryInstructionsTab />
                </>
              }
            />
          }
        >
          <Route path={TRANSACTIONS_SIGNER_SETTINGS_ROUTE} element={<SignerSettings />} />
          <Route path={TRANSACTIONS_NOTARY_INSTRUCTIONS_ROUTE} element={<NotaryInstructions />} />
          <Route index element={<Navigate to={TRANSACTIONS_SIGNER_SETTINGS_ROUTE} replace />} />
        </Route>
        <Route path={SECURITY_ROUTE} element={<SecuritySettings />}>
          <Route index element={<LoginSecurity />} />
        </Route>
        <Route path={BRAND_SETTINGS_ROOT_PREFIX} element={<BrandSettingsWrapper />}>
          <Route
            path={BRAND_SETTINGS_MAIN_ROUTE}
            element={
              <BrandSettings
                tabs={
                  <>
                    <LogoAndHeaderTab />
                    <BrandColorsTab />
                    {orgBrandNameEnabled && <CustomizeLandingPageTab />}
                  </>
                }
              />
            }
          >
            <Route path={LOGO_HEADER_BRAND_ROUTE} element={<LogoAndHeaderBrandSettings />} />
            <Route path={BRAND_COLORS_ROUTE} element={<BrandColorsSettings />} />
            {orgBrandNameEnabled && (
              <Route
                path={BRAND_SETTINGS_CUSTOMIZE_LANDING_ROUTE}
                element={<CustomizeLandingPage />}
              />
            )}
            <Route index element={<Navigate to={LOGO_HEADER_BRAND_ROUTE} replace />} />
          </Route>
          {!orgBrandNameEnabled && (
            <Route
              path={`${BRAND_SETTINGS_CUSTOMIZE_ROUTE}/*`}
              element={<WebsiteCustomization />}
            />
          )}
          <Route index element={<Navigate replace to={BRAND_SETTINGS_MAIN_ROUTE} />} />
        </Route>
        <Route path={INTEGRATIONS_ROUTE} element={<IntegrationSettingsContainer />}>
          <Route index element={<IntegrationSettings />} />
        </Route>
        <Route path={API_KEYS_SETTINGS_ROOT_PREFIX} element={<ApiKeys />} />
        {/* REAL-8187 remove user-related paths with ia-nav release  */}
        <Route path={PROFILE_ROUTE} element={<ProfileSettings tabs={<ProfileOverviewTab />} />}>
          <Route path={PROFILE_OVERVIEW_ROUTE} element={<ProfileOverview />} />
          <Route index element={<Navigate to={PROFILE_OVERVIEW_ROUTE} replace />} />
        </Route>
        <Route path={TERMS_ROUTE} element={<TermsSettings />}>
          <Route index element={<TermsAndConditions />} />
        </Route>
        <Route index element={<Navigate replace to={`${ORG_ROUTE}/${ORG_OVERVIEW_ROUTE}`} />} />
      </Routes>
    </>
  );
}
